import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Elements } from '@stripe/react-stripe-js';
import { getStripe } from '../services/stripeService';
import StripePaymentForm from './StripePaymentForm';
import { STRIPE_CONFIG } from '../config/stripe';

const PremiumUpgradeModal = ({ isOpen, onClose, onSuccess, userData }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const handlePaymentSuccess = async (paymentResult) => {
    setLoading(true);
    setError('');

    try {
      // Update user account to premium
      const userRef = doc(db, 'users', userData.uid);
      await updateDoc(userRef, {
        accountType: 'premium',
        isVerified: true,
        premiumUpgradeDate: new Date(),
        hasPaymentMethod: true,
        paymentMethodId: paymentResult.paymentMethodId,
        stripeCustomerId: paymentResult.paymentIntent?.customer || null,
        updatedAt: new Date()
      });

      // Show success message
      setPaymentSuccess(true);
      
      // Call onSuccess callback
      onSuccess();
      
      // Close modal after a delay
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Error updating user account:', error);
      setError('Payment successful but failed to update account. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setError(error.message || 'Payment failed. Please try again.');
  };

  if (!isOpen) return null;

  if (paymentSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="p-6 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Upgrade Successful!
            </h2>
            
            <p className="text-lg text-gray-600 mb-6">
              Welcome to EasyProperty Premium! You now have access to all premium features.
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-800 mb-2">Premium Benefits Activated:</h3>
              <ul className="text-sm text-green-700 space-y-1 text-left">
                <li>• Verified professional badge</li>
                <li>• Priority access to leads & job tickets</li>
                <li>• Enhanced business profile</li>
                <li>• Advanced analytics & reporting</li>
              </ul>
            </div>

            <p className="text-sm text-gray-500">
              Redirecting to dashboard...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Upgrade to Premium</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-semibold text-blue-800">Premium Benefits</span>
              </div>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Verified professional badge</li>
                <li>• Priority access to leads & job tickets</li>
                <li>• Enhanced business profile</li>
                <li>• Advanced analytics & reporting</li>
                <li>• No monthly fees - one-time payment</li>
              </ul>
            </div>

            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 mb-2">$99</div>
              <div className="text-sm text-gray-600">One-time payment</div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Stripe Elements */}
          <Elements stripe={getStripe()}>
            <StripePaymentForm
              amount={STRIPE_CONFIG.serviceProviderUpgrade}
              description={STRIPE_CONFIG.serviceProviderUpgradeDescription}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              onCancel={onClose}
              buttonText="Upgrade to Premium - $99"
              loadingText="Processing Payment..."
              metadata={{
                cardholderName: userData?.displayName || '',
                email: userData?.email || '',
                userId: userData?.uid,
                upgradeType: 'serviceProvider'
              }}
            />
          </Elements>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Your payment information is secure and encrypted by Stripe. No charges will be made until you complete the upgrade.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumUpgradeModal;
